// ========================================
// SERVICE - Projects
// Gestion des projets
// ========================================

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'projects';

/**
 * Récupérer tous les projets
 */
export const getAllProjects = async () => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    
    const projects = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: projects };
  } catch (error) {
    console.error('Erreur getAllProjects:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Récupérer un projet par son slug
 */
export const getProjectBySlug = async (slug) => {
  try {
    const q = query(collection(db, COLLECTION), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { success: false, error: 'Projet non trouvé' };
    }

    const doc = snapshot.docs[0];
    return { success: true, data: { id: doc.id, ...doc.data() } };
  } catch (error) {
    console.error('Erreur getProjectBySlug:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Récupérer les projets par catégorie
 */
export const getProjectsByCategory = async (category) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('category', '==', category),
      orderBy('order', 'asc')
    );
    const snapshot = await getDocs(q);
    
    const projects = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: projects };
  } catch (error) {
    console.error('Erreur getProjectsByCategory:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Récupérer les projets en vedette
 */
export const getFeaturedProjects = async (maxCount = 6) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('isFeatured', '==', true),
      orderBy('order', 'asc'),
      limit(maxCount)
    );
    const snapshot = await getDocs(q);
    
    const projects = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: projects };
  } catch (error) {
    console.error('Erreur getFeaturedProjects:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Ajouter un projet
 */
export const addProject = async (projectData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...projectData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Erreur addProject:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mettre à jour un projet
 */
export const updateProject = async (projectId, projectData) => {
  try {
    const docRef = doc(db, COLLECTION, projectId);
    await updateDoc(docRef, {
      ...projectData,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Erreur updateProject:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Supprimer un projet
 */
export const deleteProject = async (projectId) => {
  try {
    await deleteDoc(doc(db, COLLECTION, projectId));
    return { success: true };
  } catch (error) {
    console.error('Erreur deleteProject:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Réordonner les projets
 */
export const reorderProjects = async (projectsOrder) => {
  try {
    const updates = projectsOrder.map(async (item, index) => {
      const docRef = doc(db, COLLECTION, item.id);
      await updateDoc(docRef, { order: index });
    });

    await Promise.all(updates);
    return { success: true };
  } catch (error) {
    console.error('Erreur reorderProjects:', error);
    return { success: false, error: error.message };
  }
};

export default {
  getAllProjects,
  getProjectBySlug,
  getProjectsByCategory,
  getFeaturedProjects,
  addProject,
  updateProject,
  deleteProject,
  reorderProjects
};